import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { categoryAPI } from '../lib/api';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_percentage: 20,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setFormData({
        name: category.name,
        description: category.description,
        default_percentage: category.default_percentage,
      });
    } else {
      setSelectedCategory(null);
      setFormData({ name: '', description: '', default_percentage: 20 });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
    setFormData({ name: '', description: '', default_percentage: 20 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setSaving(true);
    try {
      if (selectedCategory) {
        await categoryAPI.update(selectedCategory.id, formData);
        toast.success('Category updated successfully');
      } else {
        await categoryAPI.create(formData);
        toast.success('Category created successfully');
      }
      fetchCategories();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    try {
      await categoryAPI.delete(selectedCategory.id);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
    }
  };

  const openDeleteDialog = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="categories-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-medium text-[#1A1A1A] tracking-tight">
              Categories
            </h1>
            <p className="text-[#585858] mt-2">
              Manage main wedding budget categories
            </p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm"
            data-testid="add-category-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Category
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E6DCD3] rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#69111F]" />
            </div>
          ) : categories.length === 0 ? (
            <div className="p-12 text-center text-[#585858]">
              <p>No categories yet. Create your first category to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F2EBE3] hover:bg-[#F2EBE3]">
                  <TableHead className="font-serif text-[#69111F] font-medium">Name</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Description</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium text-right">Default %</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id} className="hover:bg-[#FDFBF8]">
                    <TableCell className="font-medium text-[#1A1A1A]">{category.name}</TableCell>
                    <TableCell className="text-[#585858]">{category.description || '-'}</TableCell>
                    <TableCell className="text-right font-serif text-[#69111F]">
                      {category.default_percentage}%
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(category)}
                          className="text-[#69111F] hover:bg-[#69111F]/10"
                          data-testid={`edit-category-${category.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(category)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-category-${category.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-[#1A1A1A]">
                {selectedCategory ? 'Edit Category' : 'Create Category'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#1A1A1A]">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Reception"
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="category-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-[#1A1A1A]">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this category"
                    className="border-[#E6DCD3] focus:border-[#69111F] min-h-[80px]"
                    data-testid="category-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="percentage" className="text-[#1A1A1A]">Default Percentage (%)</Label>
                  <Input
                    id="percentage"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.default_percentage}
                    onChange={(e) => setFormData({ ...formData, default_percentage: parseFloat(e.target.value) || 0 })}
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="category-percentage-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  className="border-[#E6DCD3]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8]"
                  data-testid="save-category-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {selectedCategory ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-[#1A1A1A]">
                Delete Category
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#585858]">
                Are you sure you want to delete "{selectedCategory?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#E6DCD3]">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default Categories;
